import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ApiError } from '../api/errors';
import {
  createAlias,
  fetchAliasesForValue,
  fetchReferenceCandidates,
  fetchReferenceCapture,
  retireAlias,
  type ReferenceListKind,
} from '../references/api';
import type { Alias, ReferenceCandidate } from '../references/schema';
import type { DraftReferenceField, ReferenceResolution } from './document';
import { resolutionMethodLabel } from './findings';
import type { Prediction, ReferenceIssue, ReferenceKind } from './schema';
import styles from './review.module.css';

export interface ReferencePickerProps {
  readonly kind: ReferenceKind;
  /** Only for the search field's own label - the resolved/unresolved pill above this component
   * is the caller's (`ReviewScreen`/`SalesLineCard` already render it, matching 6.4's layout). */
  readonly value: DraftReferenceField;
  /** `undefined` for a row the owner added by hand - no recognition, so no ranked candidates. */
  readonly prediction: Prediction | undefined;
  /** The open reference issue blocking this exact field, if any - drives "marcar como nuevo". */
  readonly openIssue: ReferenceIssue | undefined;
  readonly editable: boolean;
  readonly onChange: (resolution: ReferenceResolution | null) => void;
}

const NOUN_BY_KIND: Readonly<Record<ReferenceKind, string>> = {
  customer: 'cliente',
  product: 'concepto',
};

const LIST_KIND_BY_KIND: Readonly<Record<ReferenceKind, ReferenceListKind>> = {
  customer: 'customers',
  product: 'products',
};

/**
 * How the owner resolves a customer/product field to (or away from) an official Excel value -
 * `docs/implementation/06-mobile-web.md` 6.5. Three ways in: a ranked candidate chip from
 * recognition, a search of the real reference list (never only the ranked candidates - a
 * candidate can be wrong), or clearing an existing resolution. Selecting only edits the local
 * draft through `onChange`; nothing here saves by itself (6.4's explicit-save convention: the
 * owner still taps "Guardar cambios"), and nothing here can make a blocked note ready - closing
 * a `reference_issue` happens server-side, in `PUT /notes/{id}/review`, only once the save round
 * trip returns it closed (`app/reviews/references.py::reconcile_open_issues`).
 *
 * Confirming an alias and retiring one are the exceptions: those call the backend immediately,
 * because an alias is its own durable record independent of this note's revision
 * (`backend/app/references/repositories.py::create_confirmed_alias`).
 */
export function ReferencePicker({
  kind,
  value,
  prediction,
  openIssue,
  editable,
  onChange,
}: ReferencePickerProps) {
  const searchInputId = useId();
  const noun = NOUN_BY_KIND[kind];
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [aliasManagerOpen, setAliasManagerOpen] = useState(false);
  const [newExplanationOpen, setNewExplanationOpen] = useState(false);
  const [aliasMessage, setAliasMessage] = useState<{
    readonly tone: 'success' | 'error';
    readonly text: string;
  } | null>(null);

  const trimmedSearch = searchTerm.trim();
  const searchQuery = useQuery({
    queryKey: ['references', kind, 'search', trimmedSearch],
    queryFn: () => fetchReferenceCandidates(kind, { search: trimmedSearch, limit: 10 }),
    enabled: editable && trimmedSearch !== '',
  });

  // A standalone staleness check, independent of the search box: the owner should see whether
  // Excel's list is trustworthy before they even start typing (`referenceIndicator` in
  // `history/statusIndicators.ts` is the same idea for the note list). Shares its query key with
  // that screen's own peek, so the two never disagree from separate cache entries.
  const captureQuery = useQuery({
    queryKey: ['references', LIST_KIND_BY_KIND[kind], 'capture'],
    queryFn: () => fetchReferenceCapture(LIST_KIND_BY_KIND[kind]),
    enabled: editable,
  });

  const aliasesQuery = useQuery({
    queryKey: ['references', 'aliases', value.referenceValueId],
    queryFn: () => fetchAliasesForValue(value.referenceValueId ?? ''),
    enabled: aliasManagerOpen && value.referenceValueId !== null,
  });

  const createAliasMutation = useMutation({ mutationFn: createAlias });

  const candidateNames = (prediction?.candidates ?? []).filter(
    (name) => name !== value.officialName,
  );

  async function resolveCandidateName(name: string): Promise<void> {
    setCandidateError(null);
    try {
      const page = await fetchReferenceCandidates(kind, { search: name, limit: 10 });
      const match = page.items.find((item) => item.official_name === name);
      if (match === undefined) {
        setCandidateError(`No se encontró "${name}" en la lista actual de Excel.`);
        return;
      }
      setSearchTerm('');
      onChange({ officialName: match.official_name, referenceValueId: match.reference_value_id });
    } catch (caught) {
      setCandidateError(
        caught instanceof ApiError ? caught.message : 'No se pudo buscar ese valor.',
      );
    }
  }

  function selectResult(candidate: ReferenceCandidate): void {
    setCandidateError(null);
    // The selected official value is now shown by the field's resolved pill. Leaving the old
    // query/results open makes it look as though the selection did not take on a phone.
    setSearchTerm('');
    onChange({
      officialName: candidate.official_name,
      referenceValueId: candidate.reference_value_id,
    });
  }

  async function handleConfirmAlias(): Promise<void> {
    if (value.referenceValueId === null || value.recognizedText.trim() === '') {
      return;
    }
    setAliasMessage(null);
    const referenceValueId = value.referenceValueId;
    const surfaceForm = value.recognizedText;
    try {
      await createAliasMutation.mutateAsync({ kind, surfaceForm, referenceValueId });
      setAliasMessage({
        tone: 'success',
        text: `Alias guardado: "${surfaceForm}" ahora significa ${value.officialName ?? ''}.`,
      });
      await queryClient.invalidateQueries({
        queryKey: ['references', 'aliases', referenceValueId],
      });
    } catch (caught) {
      setAliasMessage({
        tone: 'error',
        text: caught instanceof ApiError ? caught.message : 'No se pudo guardar el alias.',
      });
    }
  }

  const captureMissingOrStale =
    captureQuery.data === null || (captureQuery.data?.is_stale ?? false);

  return (
    <div className={styles.picker}>
      {prediction !== undefined && prediction.resolution_method !== 'unresolved' ? (
        <p className={styles.pickerMethod}>{resolutionMethodLabel(prediction.resolution_method)}</p>
      ) : null}

      {openIssue?.proposed_name != null ? (
        <p className={styles.pickerSuggestion}>
          Excel tiene algo parecido: <strong>{openIssue.proposed_name}</strong>
        </p>
      ) : null}

      {editable && candidateNames.length > 0 ? (
        <div className={styles.pickerCandidates}>
          {candidateNames.map((name) => (
            <button
              key={name}
              type="button"
              className={styles.candidateChip}
              onClick={() => {
                void resolveCandidateName(name);
              }}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}
      {candidateError !== null ? <p className={styles.fieldError}>{candidateError}</p> : null}

      {editable ? (
        <div className={styles.pickerSearch}>
          {/* Deliberately not "Buscar {noun} en Excel": that label's accessible name would
           * contain the exact-but-short label of the field above it ("Cliente"/"Concepto") as a
           * substring, which Playwright's (and a screen reader user's) name-based lookup treats
           * as ambiguous - `tests/layout/review.spec.ts` proved this the hard way for a row's
           * "Concepto" input once this search box existed alongside it. */}
          <label className={styles.label} htmlFor={searchInputId}>
            Buscar en la lista oficial de Excel
          </label>
          <input
            id={searchInputId}
            type="text"
            className={styles.input}
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
            }}
          />
          {captureMissingOrStale ? (
            <p className={styles.staleWarning}>
              {captureQuery.data === null
                ? `La lista de Excel de ${noun}s nunca se ha sincronizado. Todavía no se puede confirmar contra Excel.`
                : `La lista de Excel de ${noun}s está desactualizada; un valor nuevo podría no aparecer todavía.`}
            </p>
          ) : null}
          {searchQuery.isFetching ? <p className={styles.pickerEmpty}>Buscando…</p> : null}
          {searchQuery.data !== undefined &&
          searchQuery.data.items.length === 0 &&
          trimmedSearch !== '' ? (
            <p className={styles.pickerEmpty}>No se encontró nada con ese texto.</p>
          ) : null}
          {searchQuery.data !== undefined && searchQuery.data.items.length > 0 ? (
            <ul className={styles.pickerResults}>
              {searchQuery.data.items.map((candidate) => (
                <li key={candidate.reference_value_id} className={styles.resultRow}>
                  <span>
                    {candidate.official_name}
                    {candidate.aliases.length > 0 ? (
                      <span className={styles.resultAliases}>
                        {' '}
                        (alias: {candidate.aliases.join(', ')})
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => {
                      selectResult(candidate);
                    }}
                  >
                    Elegir
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {editable && value.referenceValueId !== null ? (
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => {
            onChange(null);
          }}
        >
          Quitar resolución
        </button>
      ) : null}

      {editable && value.referenceValueId !== null && value.recognizedText.trim() !== '' ? (
        <button
          type="button"
          className={styles.linkButton}
          disabled={createAliasMutation.isPending}
          onClick={() => {
            void handleConfirmAlias();
          }}
        >
          Guardar &quot;{value.recognizedText}&quot; como alias de {value.officialName}
        </button>
      ) : null}
      {aliasMessage !== null ? (
        <p className={aliasMessage.tone === 'success' ? styles.readOnlyNotice : styles.fieldError}>
          {aliasMessage.text}
        </p>
      ) : null}

      {value.referenceValueId !== null ? (
        <div className={styles.pickerAliasManager}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              setAliasManagerOpen((open) => !open);
            }}
          >
            {aliasManagerOpen ? 'Ocultar aliases' : 'Administrar aliases'}
          </button>
          {aliasManagerOpen ? (
            <AliasManager
              referenceValueId={value.referenceValueId}
              editable={editable}
              aliases={aliasesQuery.data}
              isLoading={aliasesQuery.isLoading}
            />
          ) : null}
        </div>
      ) : null}

      {openIssue !== undefined ? (
        <div className={styles.newValueExplanation}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              setNewExplanationOpen((open) => !open);
            }}
          >
            {newExplanationOpen ? 'Ocultar' : `¿Es un ${noun} nuevo?`}
          </button>
          {newExplanationOpen ? (
            <ol className={styles.newValueSteps}>
              <li>La nota se queda bloqueada mientras el valor no exista en Excel.</li>
              <li>La alerta sigue activa mientras tanto.</li>
              <li>Agrega el valor oficial en Excel con tu proceso normal.</li>
              <li>Después de la próxima sincronización podrás confirmarlo aquí.</li>
            </ol>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface AliasManagerProps {
  readonly referenceValueId: string;
  readonly editable: boolean;
  readonly aliases: readonly Alias[] | undefined;
  readonly isLoading: boolean;
}

/** The alias history for one official value, with a retire action - the owner's 2026-08-16
 * request that alias mappings be visible and editable in the app, not only internal state. */
function AliasManager({ referenceValueId, editable, aliases, isLoading }: AliasManagerProps) {
  const queryClient = useQueryClient();
  const retireAliasMutation = useMutation({
    mutationFn: retireAlias,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['references', 'aliases', referenceValueId] });
    },
  });

  if (isLoading) {
    return <p className={styles.pickerEmpty}>Cargando aliases…</p>;
  }
  if (aliases === undefined || aliases.length === 0) {
    return <p className={styles.pickerEmpty}>Sin aliases confirmados todavía.</p>;
  }
  return (
    <ul className={styles.aliasList}>
      {aliases.map((alias) => (
        <li key={alias.id} className={styles.aliasItem}>
          <span>
            {alias.surface_form}
            {alias.retired_at !== null ? ' (retirado)' : ''}
          </span>
          {alias.retired_at === null && editable ? (
            <button
              type="button"
              className={styles.iconButtonDanger}
              aria-label={`Retirar alias ${alias.surface_form}`}
              disabled={retireAliasMutation.isPending}
              onClick={() => {
                retireAliasMutation.mutate(alias.id);
              }}
            >
              Retirar
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
